import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Layers, Search } from 'lucide-react';
import { api } from '../../lib/api';
import type { ArtifactSummary, MediaAsset } from '../../types/api';
import { Button, IconButton, Modal, Spinner } from '../../components/ui';
import { MEDIA_LABEL, mediaIcon, useMediaUrl } from '../media/MediaCard';

/** Something picked from the user's library (any conversation) to reuse in this message. */
export interface LibraryRef { key: string; kind: 'media' | 'artifact'; id: string; label: string; detail: string }

const MEDIA_FILTERS = [['', 'All media'], ['model3d', '3D models'], ['image', 'Images'], ['video', 'Videos'], ['speech', 'Voiceovers'], ['music', 'Music']] as const;

function describeMedia(m: MediaAsset) {
  const o = m.options as { rig?: boolean; animationActionIds?: number[] };
  const extra = m.kind === 'model3d' ? [o.rig ? 'rigged' : '', o.animationActionIds?.length ? `${o.animationActionIds.length} animation${o.animationActionIds.length > 1 ? 's' : ''}` : ''].filter(Boolean).join(', ') : '';
  return `${MEDIA_LABEL[m.kind] ?? m.kind}${extra ? ` (${extra})` : ''}`;
}

/** Turns picked items into a short note the assistant can act on (its tools accept these ids). */
export function libraryNote(refs: LibraryRef[]) {
  if (!refs.length) return '';
  return `\n\n[From my library — use these existing items, don't regenerate them:\n${refs.map((r) => `- ${r.detail}: "${r.label}" (${r.kind === 'media' ? 'media_id' : 'artifact_id'}: ${r.id})`).join('\n')}]`;
}

function Thumb({ m }: { m: MediaAsset }) {
  const url = useMediaUrl(m.kind === 'image' ? m : undefined);
  return url ? <img src={url} alt="" /> : <span className="lib-item__icon">{mediaIcon(m.kind, 22)}</span>;
}

export function LibraryButton({ selected, onChange }: { selected: LibraryRef[]; onChange: (r: LibraryRef[]) => void }) {
  const [open, setOpen] = useState(false);
  return <>
    <IconButton label="Add from your library" onClick={() => setOpen(true)}><Layers size={17} /></IconButton>
    {open && <LibraryPicker selected={selected} onDone={(r) => { onChange(r); setOpen(false); }} onClose={() => setOpen(false)} />}
  </>;
}

function LibraryPicker({ selected, onDone, onClose }: { selected: LibraryRef[]; onDone: (r: LibraryRef[]) => void; onClose: () => void }) {
  const [tab, setTab] = useState<'media' | 'artifacts'>('media');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<LibraryRef[]>(selected);
  const media = useQuery({ queryKey: ['library-media'], queryFn: () => api<{ media: MediaAsset[] }>('/api/media').then((r) => r.media.filter((m) => m.status === 'completed')) });
  const artifacts = useQuery({ queryKey: ['library-artifacts'], enabled: tab === 'artifacts', queryFn: () => api<{ artifacts: ArtifactSummary[] }>('/api/artifacts').then((r) => r.artifacts) });
  const needle = q.trim().toLowerCase();
  const mediaList = useMemo(() => (media.data ?? []).filter((m) => (!kind || m.kind === kind) && (!needle || m.prompt.toLowerCase().includes(needle))), [media.data, kind, needle]);
  const artifactList = useMemo(() => (artifacts.data ?? []).filter((a) => !needle || a.title.toLowerCase().includes(needle)), [artifacts.data, needle]);
  const toggle = (r: LibraryRef) => setPicked((p) => (p.some((x) => x.key === r.key) ? p.filter((x) => x.key !== r.key) : [...p, r]));
  const isOn = (key: string) => picked.some((x) => x.key === key);
  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose(); }} title="Add from your library" wide
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={() => onDone(picked)}>Add {picked.length || ''}</Button></>}>
      <div className="lib">
        <div className="lib__bar">
          <div className="lib__tabs" role="tablist">
            <button role="tab" aria-selected={tab === 'media'} className={tab === 'media' ? 'is-on' : ''} onClick={() => setTab('media')}>Media</button>
            <button role="tab" aria-selected={tab === 'artifacts'} className={tab === 'artifacts' ? 'is-on' : ''} onClick={() => setTab('artifacts')}>Artifacts</button>
          </div>
          {tab === 'media' && <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Media type">{MEDIA_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}
          <label className="lib__search"><Search size={15} /><input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} /></label>
        </div>
        <p className="muted small">Pick things you made in any chat. Lumnos reuses them instead of making new ones.</p>
        {tab === 'media' ? (media.isLoading ? <Spinner /> : !mediaList.length ? <p className="muted">Nothing here yet.</p> :
          <div className="lib__grid">{mediaList.map((m) => {
            const r: LibraryRef = { key: `m:${m.id}`, kind: 'media', id: m.id, label: m.prompt.slice(0, 80) || MEDIA_LABEL[m.kind], detail: describeMedia(m) };
            return <button key={m.id} className={`lib-item${isOn(r.key) ? ' is-on' : ''}`} onClick={() => toggle(r)} aria-pressed={isOn(r.key)}>
              <span className="lib-item__thumb"><Thumb m={m} />{isOn(r.key) && <span className="lib-item__check"><Check size={14} /></span>}</span>
              <span className="lib-item__name">{r.label}</span><span className="lib-item__sub">{r.detail}</span>
            </button>;
          })}</div>)
        : (artifacts.isLoading ? <Spinner /> : !artifactList.length ? <p className="muted">No artifacts yet.</p> :
          <div className="lib__list">{artifactList.map((a) => {
            const r: LibraryRef = { key: `a:${a.id}`, kind: 'artifact', id: a.id, label: a.title, detail: `${a.type} artifact` };
            return <button key={a.id} className={`lib-row${isOn(r.key) ? ' is-on' : ''}`} onClick={() => toggle(r)} aria-pressed={isOn(r.key)}>
              <span>{isOn(r.key) ? <Check size={15} /> : <Layers size={15} />}</span><strong>{a.title}</strong><span className="muted small">{a.type}{a.versionNumber ? ` · v${a.versionNumber}` : ''}</span>
            </button>;
          })}</div>)}
      </div>
    </Modal>
  );
}
